'use client'
import {
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-IJKZ5OMO.mjs";

// src/ui/components/common/BookingWrapper.tsx
import { useRef } from "react";
import { AnimatePresence, motion } from "motion/react";
import { twMerge } from "tailwind-merge";
import { useRouter } from "@wbk/config";
import { jsx } from "react/jsx-runtime";
var BookingWrapper = (_a) => {
  var _b = _a, {
    initialMode,
    mode,
    modes = ["tickets", "extra", "summary"],
    className,
    children,
    ref
  } = _b, props = __objRest(_b, [
    "initialMode",
    "mode",
    "modes",
    "className",
    "children",
    "ref"
  ]);
  const { locale } = useRouter();
  const direction = (locale == null ? void 0 : locale.startsWith("ar")) ? -100 : 100;
  const nextModeRef = useRef(initialMode || modes[0]);
  const prevModeRef = useRef(initialMode || modes[0]);
  return /* @__PURE__ */ jsx(AnimatePresence, { initial: false, mode: "wait", children: /* @__PURE__ */ jsx(
    motion.div,
    __spreadProps(__spreadValues({
      ref,
      className: twMerge(
        "min-w-0 max-w-full flex-1 transition-all data-[overflow=true]:overflow-visible lg:space-y-1",
        className
      ),
      initial: "enter",
      "data-overflow": mode === "addons",
      animate: "center",
      exit: "exit",
      custom: { mode },
      variants: {
        enter: ({ mode: mode2 }) => {
          nextModeRef.current = mode2;
          const prevModeIndex = modes.indexOf(prevModeRef.current);
          const nextModeIndex = modes.indexOf(nextModeRef.current);
          const sign = Math.sign(nextModeIndex - prevModeIndex);
          return {
            x: direction * sign,
            opacity: 0
          };
        },
        center: ({ mode: mode2 }) => {
          prevModeRef.current = mode2;
          return {
            x: 0,
            opacity: 1
          };
        },
        exit: () => {
          const prevModeIndex = modes.indexOf(prevModeRef.current);
          const nextModeIndex = modes.indexOf(nextModeRef.current);
          const sign = Math.sign(prevModeIndex - nextModeIndex);
          return {
            x: sign * direction,
            opacity: 0
          };
        }
      },
      transition: { duration: 0.2 }
    }, props), {
      children
    }),
    `${mode}-${direction}`
  ) }, mode);
};
var BookingWrapper_default = BookingWrapper;

// src/ui/components/common/PaymentFailed.tsx
import { useEffect, useState } from "react";
import { Button as Button2, Typography as Typography2 } from "@wbk/ui";
import { Exception } from "@wbk/logger";
import { readCookie } from "@wbk/utils";
import { Link as Link2, useTranslation as useTranslation2 } from "@wbk/config";
import { ExclamationTriangleIcon } from "@wbk/svg";
import { useAnalytics as useAnalytics2 } from "@wbk/analytics";

// src/ui/components/common/contact/WbkContactUs.tsx
import { useAnalytics } from "@wbk/analytics";
import { useGetOrganizationBySlug } from "@wbk/api/hooks";
import { Link, Trans, useRouter as useRouter2, useShowContactUs, useTranslation } from "@wbk/config";
import { Mail, SupportHeadphone, TelephoneIcon, Whatsapp } from "@wbk/svg";
import { Button, ResponsiveModal, Typography } from "@wbk/ui";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var WbkContactUs = ({ render, slug }) => {
  const { t } = useTranslation("ticketing_common");
  const { locale, params, pathname } = useRouter2();
  const { trackEvent } = useAnalytics();
  const { showContactUs } = useShowContactUs();
  const { data: organization } = useGetOrganizationBySlug(
    { slug: slug || "", lang: locale },
    { enabled: !!slug, staleTime: 6e4 * 10 }
  );
  const zendeskSubmitUrl = (organization == null ? void 0 : organization.help_center_url) || `https://wbk.zendesk.com/hc/${locale}`;
  if (!showContactUs) return null;
  return /* @__PURE__ */ jsx2(
    ResponsiveModal,
    {
      "data-testid": "ticketing_modal_contact_us",
      title: /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
        t("ticketing_common:contact_us.title"),
        /* @__PURE__ */ jsx2(Typography, { as: "div", color: "text-secondary", children: t("ticketing_common:contact_us.description") })
      ] }),
      size: "2xl",
      cancel: false,
      action: {
        label: t("ticketing_common:contact_us.submit"),
        onClick: () => {
          trackEvent({
            event: "request_submit_clicked",
            data: {
              slug: params == null ? void 0 : params.queuedSlug
            }
          });
          window.location.href = zendeskSubmitUrl;
        }
      },
      render: render ? render : ({ open }) => /* @__PURE__ */ jsx2("div", { className: "space-y-4", children: /* @__PURE__ */ jsxs(
        Button,
        {
          "data-testid": "ticketing_contact_us_button",
          className: "space-x-1 p-1 rtl:space-x-reverse",
          type: "button",
          theme: "white",
          shape: "text",
          onClick: () => {
            trackEvent({
              event: "contact_us_clicked",
              data: {
                slug: params == null ? void 0 : params.queuedSlug,
                page_name: pathname
              }
            });
            open();
          },
          children: [
            /* @__PURE__ */ jsx2(SupportHeadphone, { className: "text-text-secondary h-6 w-6" }),
            /* @__PURE__ */ jsx2(Typography, { variant: "link-L", color: "text-secondary", children: t("ticketing_common:contact_us.need_help") })
          ]
        }
      ) }),
      body: () => /* @__PURE__ */ jsx2(ContactInfoBody, { slug })
    }
  );
};
var ContactInfoBody = ({ slug }) => {
  const { t } = useTranslation("ticketing_common");
  const { locale } = useRouter2();
  const { data: organization, isLoading: isOrganizationLoading } = useGetOrganizationBySlug(
    {
      slug: slug || "",
      lang: locale
    },
    { enabled: !!slug, staleTime: 6e4 * 10 }
  );
  if (isOrganizationLoading) {
    return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
      /* @__PURE__ */ jsxs("div", { className: "border-paper space-y-4 rounded-md", children: [
        /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-3/5 animate-pulse" }),
        /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex justify-between", children: [
            /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-1/3 animate-pulse" }),
            /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-1/4 animate-pulse" })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex justify-between", children: [
            /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-1/3 animate-pulse" }),
            /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-1/4 animate-pulse" })
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "space-y-4", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
          /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-3 shrink-0 animate-pulse" }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-1 justify-between", children: [
            /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-1/4 animate-pulse" }),
            /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-1/3 animate-pulse" })
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
          /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-3 shrink-0 animate-pulse" }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-1 justify-between", children: [
            /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-1/4 animate-pulse" }),
            /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-1/3 animate-pulse" })
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-start space-y-1", children: [
        /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-1/2 animate-pulse" }),
        /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-2 rounded-S relative h-3 w-1/3 animate-pulse" })
      ] })
    ] });
  }
  const showOrgInfo = !!(organization == null ? void 0 : organization.phone) || !!(organization == null ? void 0 : organization.email) || !!(organization == null ? void 0 : organization.whatsapp);
  const orgPhone = (organization == null ? void 0 : organization.phone) || "";
  const orgEmail = (organization == null ? void 0 : organization.email) || "";
  const orgWhatsapp = (organization == null ? void 0 : organization.whatsapp) || "";
  const orgWorkingHours = (organization == null ? void 0 : organization.working_hours) || "";
  const orgHelpCenterFAQUrl = (organization == null ? void 0 : organization.help_center_faq_url) || (organization == null ? void 0 : organization.help_center_url) || "";
  const supportPhone = showOrgInfo ? orgPhone : "+966920035043";
  const supportPhoneLabel = showOrgInfo ? orgPhone : "+966 920035043";
  const allYearTime = orgWorkingHours || t("ticketing_common:contact_us.all_year_time");
  const zendeskUrl = orgHelpCenterFAQUrl || `https://wbk.zendesk.com/hc/${locale}`;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsxs("div", { className: "border-paper space-y-4 rounded-md border", children: [
      /* @__PURE__ */ jsx2(Typography, { variant: "heading-S", children: t("ticketing_common:contact_us.working_hours") }),
      /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex justify-between", children: [
          /* @__PURE__ */ jsx2(Typography, { children: t("ticketing_common:contact_us.all_year") }),
          /* @__PURE__ */ jsx2(Typography, { children: allYearTime })
        ] }),
        !showOrgInfo && !orgWorkingHours && /* @__PURE__ */ jsxs("div", { className: "flex justify-between", children: [
          /* @__PURE__ */ jsx2(Typography, { children: t("ticketing_common:contact_us.ramadan_hours") }),
          /* @__PURE__ */ jsx2(Typography, { children: t("ticketing_common:contact_us.ramadan_day_time") })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "space-y-4", children: [
      !!orgWhatsapp && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx2(Whatsapp, { className: "h-6 w-6 shrink-0" }),
        /* @__PURE__ */ jsx2("div", { className: "flex flex-1 justify-between", children: /* @__PURE__ */ jsxs(
          Trans,
          {
            i18nKey: "ticketing_common:contact_us.whatsapp",
            values: { number: orgWhatsapp },
            children: [
              /* @__PURE__ */ jsx2(Typography, { variant: "body-L", strong: true }),
              /* @__PURE__ */ jsx2(
                Typography,
                {
                  as: "a",
                  href: `https://wa.me/${orgWhatsapp.replace(/\D/g, "")}`,
                  target: "_blank",
                  variant: "link-L",
                  dir: "ltr",
                  className: "underline"
                }
              )
            ]
          }
        ) })
      ] }),
      !!supportPhone && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx2(TelephoneIcon, { className: "h-6 w-6 shrink-0" }),
        /* @__PURE__ */ jsx2("div", { className: "flex flex-1 justify-between", children: /* @__PURE__ */ jsxs(
          Trans,
          {
            i18nKey: "ticketing_common:contact_us.phone",
            values: { number: supportPhoneLabel },
            children: [
              /* @__PURE__ */ jsx2(Typography, { variant: "body-L", strong: true }),
              /* @__PURE__ */ jsx2(
                Typography,
                {
                  as: "a",
                  href: `tel:${supportPhone}`,
                  target: "_blank",
                  variant: "link-L",
                  dir: "ltr",
                  className: "underline"
                }
              )
            ]
          }
        ) })
      ] }),
      !!orgEmail && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx2(Mail, { className: "h-6 w-6 shrink-0" }),
        /* @__PURE__ */ jsx2("div", { className: "flex flex-1 justify-between", children: /* @__PURE__ */ jsxs(Trans, { i18nKey: "ticketing_common:contact_us.email", values: { email: orgEmail }, children: [
          /* @__PURE__ */ jsx2(Typography, { variant: "body-L", strong: true }),
          /* @__PURE__ */ jsx2(
            Typography,
            {
              as: "a",
              href: `mailto:${orgEmail}`,
              target: "_blank",
              variant: "link-L",
              dir: "ltr",
              className: "underline"
            }
          )
        ] }) })
      ] })
    ] }),
    /* @__PURE__ */ jsx2("div", { className: "flex flex-col items-start space-y-1", children: /* @__PURE__ */ jsxs(Trans, { i18nKey: "ticketing_common:contact_us.faq", children: [
      /* @__PURE__ */ jsx2(Typography, {}),
      /* @__PURE__ */ jsx2(
        Button,
        {
          theme: "transparent",
          shape: "text",
          className: "p-0 underline",
          as: Link,
          href: zendeskUrl
        }
      )
    ] }) })
  ] });
};
var WbkContactUs_default = WbkContactUs;

// src/ui/components/common/PaymentFailed.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var PaymentFailed = ({ secondaryAction }) => {
  var _a, _b;
  const [lastBookPage, setLastBookPage] = useState("/");
  const { t } = useTranslation2("ticketing_booking");
  const searchParams = new URLSearchParams(window.location.search);
  const reason = (_b = (_a = searchParams.get("error")) == null ? void 0 : _a.split(":")) == null ? void 0 : _b[1];
  const { purchaseFailed } = useAnalytics2();
  useEffect(() => {
    const fireSentry = setTimeout(() => {
      const error = new Error(reason || "Reason not found in URL");
      error.name = "PaymentFailedException";
      new Exception({
        error,
        name: "PaymentFailedException",
        context: {
          extra: { reason: reason || "EMPTY" },
          tags: { journey: "checkout" }
        }
      });
    }, 500);
    return () => clearTimeout(fireSentry);
  }, [reason]);
  useEffect(() => {
    purchaseFailed({ reason });
    setLastBookPage(readCookie("last_book_page") || "/");
  }, []);
  return /* @__PURE__ */ jsxs2("section", { className: "container py-20", children: [
    /* @__PURE__ */ jsx3(ExclamationTriangleIcon, { className: "h-16 w-16" }),
    /* @__PURE__ */ jsxs2("div", { className: "mt-2", children: [
      /* @__PURE__ */ jsx3(Typography2, { variant: "heading-XL", className: "mb-2", children: t("ticketing_booking:payment_failed") }),
      /* @__PURE__ */ jsx3(Typography2, { as: "div", className: "max-w-3xl py-2", children: reason ? /* @__PURE__ */ jsxs2("span", { className: "py-3", children: [
        /* @__PURE__ */ jsx3(Typography2, { children: t("ticketing_booking:reason_bank") }),
        /* @__PURE__ */ jsx3(Typography2, { color: "error", children: `${reason}` })
      ] }) : t("ticketing_booking:payment_information_right") }),
      /* @__PURE__ */ jsx3(WbkContactUs_default, {}),
      /* @__PURE__ */ jsxs2("div", { className: "mt-4 flex gap-4 py-4", children: [
        /* @__PURE__ */ jsx3(Button2, { as: Link2, href: lastBookPage, theme: "action", children: t("ticketing_booking:try_again") }),
        secondaryAction && /* @__PURE__ */ jsx3(Button2, { as: Link2, href: secondaryAction.href, shape: "outlined", theme: "action", children: secondaryAction.label })
      ] })
    ] })
  ] });
};
var PaymentFailed_default = PaymentFailed;

export {
  BookingWrapper_default,
  WbkContactUs_default,
  PaymentFailed_default
};
//# sourceMappingURL=chunk-4CB2E3SW.mjs.map